import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import PublicIcon from '@material-ui/icons/Public';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import GroupIcon from '@material-ui/icons/Group';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Container from 'common/Container';

const DEFAULT_AXES = [
  {
    title: 'Streaming',
    text: 'El impacto del vivo y la conexión humana.',
    accent: '#e53935',
    soft: 'rgba(229, 57, 53, 0.08)',
    Icon: LiveTvIcon,
  },
  {
    title: 'On-Demand',
    text: 'El catálogo original disponible en todo momento.',
    accent: '#5e35b1',
    soft: 'rgba(94, 53, 177, 0.08)',
    Icon: VideoLibraryIcon,
  },
  {
    title: 'Digest',
    text: 'La inteligencia editorial para quienes buscan profundidad.',
    accent: '#00695c',
    soft: 'rgba(0, 105, 92, 0.08)',
    Icon: MenuBookIcon,
  },
];

const CIRCUIT_ICON_MAP = {
  business: BusinessCenterIcon,
  growth: TrendingUpIcon,
  target: TrackChangesIcon,
  ecosystem: PublicIcon,
};

const HIGHLIGHT_ICON_MAP = {
  streaming: LiveTvIcon,
  reach: EqualizerIcon,
  followers: GroupIcon,
  visibility: VisibilityIcon,
};

const DEFAULT_HIGHLIGHT_ICONS = [LiveTvIcon, EqualizerIcon, GroupIcon, VisibilityIcon];

const TnoEcosystemSection = ({ section = {} }) => {
  const title = section.title || 'El ecosistema TNO';
  const subtitle = section.subtitle;
  const description =
    section.description ||
    'Somos un ecosistema de comunicación integral que no se detiene. Unimos la inmediatez con la profundidad a través de tres ejes:';
  const axes = Array.isArray(section.axes) && section.axes.length > 0 ? section.axes : DEFAULT_AXES;
  const highlights = Array.isArray(section.highlights) ? section.highlights : [];
  const circuit = section.circuit || null;
  const circuitItems = Array.isArray(circuit?.items) ? circuit.items : [];

  return (
    <Container>
      <Box py={{ xs: 5, md: 7 }} marginBottom={2}>
        <Typography variant="h3" sx={{ fontWeight: 800 }} gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 500, mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        <Typography variant="body1" color="textSecondary" sx={{ lineHeight: 1.9, maxWidth: 900, mb: 4 }}>
          {description}
        </Typography>
        <Grid container spacing={3}>
          {axes.map((item) => {
            const AxisIcon = item.Icon || LiveTvIcon;
            return (
              <Grid item xs={12} md={4} key={item.title}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 3,
                    height: '100%',
                    borderLeft: '4px solid',
                    borderLeftColor: item.accent,
                    bgcolor: item.soft,
                  }}
                >
                  <Box
                    width={48}
                    height={48}
                    borderRadius="50%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                    sx={{ bgcolor: item.accent, color: 'common.white' }}
                  >
                    <AxisIcon />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
                    {item.title}
                  </Typography>
                  <Typography color="textSecondary">{item.text}</Typography>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {highlights.length > 0 && (
          <Box mt={4} mb={1}>
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1 }} display="block" gutterBottom>
              Métricas TNO
            </Typography>
            <Grid container spacing={2}>
              {highlights.map((item, index) => {
                const HighlightIcon =
                  HIGHLIGHT_ICON_MAP[item.icon] || DEFAULT_HIGHLIGHT_ICONS[index % DEFAULT_HIGHLIGHT_ICONS.length];
                return (
                  <Grid item xs={12} sm={6} md={3} key={item.label || index}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2.2,
                        height: '100%',
                        borderRadius: 2,
                        borderColor: 'rgba(25, 118, 210, 0.16)',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box
                        width={36}
                        height={36}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mb={1.1}
                        sx={{ bgcolor: 'rgba(25, 118, 210, 0.12)', color: 'primary.main' }}
                      >
                        <HighlightIcon fontSize="small" />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', mb: 0.6 }}>
                        {item.value}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6 }}>
                        {item.label}
                      </Typography>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {circuit && (
          <Box
            mt={4}
            sx={{
              p: { xs: 2.2, md: 3 },
              borderRadius: 3,
              border: '1px solid rgba(25, 118, 210, 0.16)',
              bgcolor: 'rgba(25, 118, 210, 0.04)',
            }}
          >
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 1 }}>
              {circuit.overline || 'Circuito de nicho'}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, mb: 1.25 }}>
              {circuit.title}
            </Typography>
            {circuit.description && (
              <Typography color="textSecondary" sx={{ lineHeight: 1.8, mb: circuitItems.length > 0 ? 2.2 : 0 }}>
                {circuit.description}
              </Typography>
            )}

            {circuitItems.length > 0 && (
              <Grid container spacing={2}>
                {circuitItems.map((item, index) => {
                  const IconComp = CIRCUIT_ICON_MAP[item.icon] || BusinessCenterIcon;
                  return (
                    <Grid item xs={12} sm={6} md={3} key={item.title || index}>
                      <Card
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: '100%',
                          borderRadius: 2,
                          borderColor: 'rgba(25, 118, 210, 0.16)',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box
                          width={38}
                          height={38}
                          borderRadius="50%"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          mb={1.1}
                          sx={{ bgcolor: 'rgba(25, 118, 210, 0.12)', color: 'primary.main' }}
                        >
                          <IconComp fontSize="small" />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.6 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6 }}>
                          {item.text}
                        </Typography>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

TnoEcosystemSection.propTypes = {
  section: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    description: PropTypes.string,
    axes: PropTypes.array,
    highlights: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string,
        value: PropTypes.string,
        label: PropTypes.string,
      })
    ),
    circuit: PropTypes.shape({
      overline: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          icon: PropTypes.string,
          title: PropTypes.string,
          text: PropTypes.string,
        })
      ),
    }),
  }),
};

export default TnoEcosystemSection;
